"use client";
import { OptimizedImage } from "../../../components/common/OptimizedImage";
import { useCallback, useState } from "react";
import dynamic from "next/dynamic";
import { Camera } from "lucide-react";

const GalleryPopup = dynamic<any>(() => import("../elements/GalleryPopup"), {
  ssr: false,
});

interface AttractionImage {
  Image: string;
}

interface GallerySectionProps {
  images: AttractionImage[];
  title: string;
}

export default function GallerySection({ images, title }: GallerySectionProps) {
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);

  const handleImageClick = useCallback((index: number) => {
    setCurrentGalleryIndex(index);
    setGalleryOpen(true);
  }, []);

  const handleGalleryClose = useCallback(() => setGalleryOpen(false), []);
  const handleGalleryIndexChange = useCallback((index: number) => {
    setCurrentGalleryIndex(index);
  }, []);

  if (!images || images.length === 0) return null;

  return (
    <section className="py-24 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl md:text-4xl mb-12 text-center text-[#1A1A1A]">
          Gallery
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {images.map((image, index) => (
            <div
              key={index}
              className="aspect-[4/3] rounded-xl overflow-hidden shadow-lg group cursor-pointer relative"
              onClick={() => handleImageClick(index)}
            >
              <OptimizedImage
                src={image.Image}
                alt={`${title} Image ${index + 1}`}
                className="w-full h-full group-hover:scale-110 transition-transform duration-700"
                aspectRatio="4/3"
              />
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="bg-black/50 rounded-full p-4">
                  <Camera className="h-8 w-8 text-white" />
                </div>
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
          ))}
        </div>
      </div>
      {galleryOpen && (
        <GalleryPopup
          images={images}
          currentIndex={currentGalleryIndex}
          isOpen={galleryOpen}
          onClose={handleGalleryClose}
          onIndexChange={handleGalleryIndexChange}
        />
      )}
    </section>
  );
}
